// src/pages/Notes/index.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  New. Reports where the work is, instead of in his inbox.
//
// Grouped by stage rather than dragged between columns. Tyler described
// dragging cards and that is where this goes, but a board you can drag is a
// week of work and a board you can read is an afternoon, and the thing that
// actually matters on day one is that he can tell what is waiting on him.
//
// Waiting on him comes first and always. Everything else is us.

import { useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Badge, Spinner, Alert, AlertIcon } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronRight, FileText, MessageSquare, Circle } from 'lucide-react'
import { listNotes, STAGE_COPY } from '../../lib/notes'

const HIS = ['sent', 'read', 'discussing']
const OURS = ['approved', 'building']
const REST = ['parked', 'done']

function when(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function Card({ note }) {
  const unread = !note.read_at
  return (
    <HStack
      as={RouterLink}
      to={`/notes/${note.slug}/`}
      align="flex-start"
      spacing={4}
      py={5}
      borderTop="1px solid"
      borderColor="line"
      _hover={{ textDecoration: 'none', '& .go': { transform: 'translateX(3px)' } }}
    >
      <Box mt={1} flexShrink={0} color={unread ? 'accent' : 'inkDim'}>
        {unread ? <Circle size={9} fill="currentColor" /> : <FileText size={16} />}
      </Box>

      <Box flex={1} minW={0}>
        <HStack spacing={2} mb={0.5} flexWrap="wrap">
          <Text fontWeight={600} color="ink" letterSpacing="-0.01em">
            {note.title}
          </Text>
          <Badge variant="subtle">{note.kind}</Badge>
        </HStack>
        {note.summary && (
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} noOfLines={2}>
            {note.summary}
          </Text>
        )}
        <HStack spacing={3} mt={1.5} color="inkDim" fontSize="sm" flexWrap="wrap">
          <Text>{when(note.published_at)}</Text>
          {note.sheets ? <Text>{note.sheets} sheets</Text> : null}
          {note.entry_count > 0 && (
            <HStack spacing={1}>
              <MessageSquare size={13} />
              <Text>{note.entry_count}</Text>
            </HStack>
          )}
          {unread && <Text color="accent">not opened yet</Text>}
        </HStack>
      </Box>

      <Box className="go" color="inkDim" mt={1} flexShrink={0} transition="transform 0.15s ease">
        <ChevronRight size={16} />
      </Box>
    </HStack>
  )
}

function Group({ title, hint, notes }) {
  if (notes.length === 0) return null
  return (
    <Box>
      <Text fontSize="lg" fontWeight={600} mb={0.5}>
        {title}
      </Text>
      {hint && (
        <Text fontSize="body" color="inkMuted" mb={2} maxW="measure">
          {hint}
        </Text>
      )}
      <Flex direction="column">
        {notes.map((n) => (
          <Card key={n.id} note={n} />
        ))}
      </Flex>
    </Box>
  )
}

export default function Notes() {
  const [state, setState] = useState({ loading: true, notes: [], error: null })

  useEffect(() => {
    let alive = true
    listNotes().then(({ data, error }) => {
      if (!alive) return
      setState({ loading: false, notes: data, error: error ? error.message : null })
    })
    return () => {
      alive = false
    }
  }, [])

  const pick = (stages) => state.notes.filter((n) => stages.includes(n.stage))

  return (
    <VStack align="stretch" spacing={8} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          From the coach
        </Text>
        <Text textStyle="pageTitle">Notes</Text>
      </Box>

      {state.loading && (
        <HStack spacing={3} color="inkMuted">
          <Spinner size="sm" />
          <Text fontSize="body">Loading</Text>
        </HStack>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && state.notes.length === 0 && (
        <VStack align="flex-start" spacing={2} py={10}>
          <FileText size={22} />
          <Text fontSize="lg" fontWeight={600}>
            Nothing here yet
          </Text>
          <Text fontSize="body" color="inkMuted" maxW="measure">
            Reports and plans land here rather than in an inbox, so they can be corrected in place and
            answered without writing an email.
          </Text>
        </VStack>
      )}

      {!state.loading && !state.error && state.notes.length > 0 && (
        <>
          <Group
            title="Waiting on you"
            hint="Read it, say what you think, or tell us to build it."
            notes={pick(HIS)}
          />
          <Group title="With us" hint="Approved and in progress." notes={pick(OURS)} />
          <Group
            title="Parked and done"
            hint={STAGE_COPY.parked.hint}
            notes={pick(REST)}
          />
        </>
      )}
    </VStack>
  )
}
